import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateTaxonomyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Input for the optimistic-concurrency taxonomy update (RSP-6488).
 * `OriginalTimestamp` is the last-seen `ModifiedAtTimestamp`, threaded RAW —
 * never reformatted (a fresh Date would defeat the concurrency check).
 */
export interface UpdateTaxonomyInput {
  Id: string;
  Common: JSONB;
  Library: JSONB;
  Rating: JSONB;
  Taxonomy: JSONB;
  InternalAuditRating: JSONB;
  OversightRating: JSONB;
  OriginalTimestamp?: string | null;
}

/** Reshapes the tRPC `{ affected_rows }` result to the GraphQL mutation shape. */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpdateTaxonomyMutation => ({
  update_taxonomy: {
    __typename: 'taxonomy_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useUpdateTaxonomyTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.taxonomy.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.taxonomy.taxonomyAudit.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.taxonomy.taxonomyByLocale.queryKey(),
        });
      },
    }),
  });

  return {
    updateTaxonomy: async (
      input: UpdateTaxonomyInput
    ): Promise<UpdateTaxonomyMutation> => {
      const result = await mutation.mutateAsync(input);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
